import { useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import { applyAuthSession } from '../utils/applyAuthSession';
import { clearClientAuthState } from '../utils/authSessionCoordinator';
import {
  allowSilentRefresh,
  invalidateAuthSession,
} from '../utils/invalidateAuthSession';
import {
  login as loginApi,
  getCurrentUser as getCurrentUserApi,
  verifyOtpApi,
  verifyTwoFactorApi,
  disableTwoFactorApi,
} from '../api/auth';

export type LoginHookResult =
  | { success: true }
  | { success: true; otpRequired: true; otpSessionId: string }
  | { success: false; error: string };

export function useAuth() {
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);
  const isAuthenticated = token !== null && token.length > 0;

  const fetchCurrentUser = useCallback(async (): Promise<
    { success: true } | { success: false; error: string }
  > => {
    const result = await getCurrentUserApi();
    if (result.success) {
      setUser(result.user);
      return { success: true };
    }
    // Do not invalidate here: the API client 401 interceptor coordinates refresh
    // and login redirect. Clearing the session here caused silent re-bootstrap loops.
    return { success: false, error: result.error };
  }, [setUser]);

  function applySession(accessToken: string, csrfToken: string, expiresInSeconds?: number | null) {
    applyAuthSession(accessToken, csrfToken, expiresInSeconds);
  }

  async function login(
    email: string,
    password: string
  ): Promise<LoginHookResult> {
    clearClientAuthState();
    allowSilentRefresh();
    const result = await loginApi({ email, password });
    if (result.success && 'otpRequired' in result) {
      return { success: true, otpRequired: true, otpSessionId: result.otpSessionId };
    }
    if (result.success) {
      applySession(result.accessToken, result.csrfToken, result.expiresInSeconds);
      const profile = await fetchCurrentUser();
      if (!profile.success) {
        return { success: false, error: profile.error };
      }
      return { success: true };
    }
    return { success: false, error: result.error };
  }

  async function verifyOtp(
    otpSessionId: string,
    code: string
  ): Promise<{ success: true } | { success: false; error: string }> {
    allowSilentRefresh();
    const result = await verifyOtpApi(otpSessionId, code);
    if (result.success) {
      applySession(result.accessToken, result.csrfToken, result.expiresInSeconds);
      const profile = await fetchCurrentUser();
      if (!profile.success) {
        return { success: false, error: profile.error };
      }
      return { success: true };
    }
    return { success: false, error: result.error };
  }

  async function logout(): Promise<void> {
    await invalidateAuthSession();
  }

  async function enableTwoFactor(
    otpSessionId: string,
    code: string
  ): Promise<{ success: true } | { success: false; error: string }> {
    const result = await verifyTwoFactorApi(otpSessionId, code);
    if (result.success) {
      await fetchCurrentUser();
      return { success: true };
    }
    return { success: false, error: result.error };
  }

  async function disableTwoFactor(): Promise<
    { success: true } | { success: false; error: string }
  > {
    const result = await disableTwoFactorApi();
    if (result.success) {
      await fetchCurrentUser();
      return { success: true };
    }
    return { success: false, error: result.error };
  }

  return {
    token,
    user,
    isAuthenticated,
    fetchCurrentUser,
    login,
    verifyOtp,
    logout,
    enableTwoFactor,
    disableTwoFactor,
  };
}
